import { useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Users, Plus, X, UserCheck, UserX, Phone, Building2, Stethoscope, Search, Camera, Pencil, PauseCircle, PlayCircle, Trash2, AlertTriangle } from 'lucide-react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { CircleStencil, Cropper, CropperRef } from 'react-advanced-cropper';
import 'react-advanced-cropper/dist/style.css';
import { toast } from 'sonner';
import api from '@/lib/axios';
import { getInitials } from '@/lib/utils';
import { useAuthStore, ROL } from '@/store/auth.store';
import especialidadesJson from '@/assets/json/especialidades.json';
import { ComboboxInput } from '@/components/ui/ComboboxInput';
import { EspecialidadesManager } from '@/components/personal/EspecialidadesManager';
import { getErrorMessage } from '@/lib/errors';

const ESPECIALIDADES: string[] = especialidadesJson
  .map(e => e.Nombre)
  .filter(n => n !== 'Ninguna')
  .sort((a, b) => a.localeCompare(b, 'es'));

/* ─ Constantes ───────────────────────────────────────────────────────────── */
const ROL_LABEL: Record<string, string> = {
  rolsadm01x: 'Super Admin',
  roladmcl02: 'Administrador',
  rolmedc03y: 'Médico',
  rolasst04z: 'Asistente',
  rolrecp05w: 'Recepcionista',
};

const ROL_COLOR: Record<string, string> = {
  rolsadm01x: 'bg-red-50 text-red-700 border-red-100',
  roladmcl02: 'bg-orange-50 text-orange-700 border-orange-100',
  rolmedc03y: 'bg-blue-50 text-blue-700 border-blue-100',
  rolasst04z: 'bg-green-50 text-green-700 border-green-100',
  rolrecp05w: 'bg-purple-50 text-purple-700 border-purple-100',
};

const ROLES_DISPONIBLES = [
  { value: ROL.ADMIN_CLINICA, label: 'Administrador' },
  { value: ROL.MEDICO,        label: 'Médico' },
  { value: ROL.MEDICO_ADMIN,  label: 'Médico Administrador' },
  { value: ROL.ASISTENTE,     label: 'Asistente' },
  { value: ROL.RECEPCIONISTA, label: 'Recepcionista' },
];

const SEXO_OPTS = [
  { value: 'masculino', label: 'Masculino' },
  { value: 'femenino',  label: 'Femenino' },
  { value: 'otro',      label: 'Otro' },
];

const emptyForm = {
  nombre: '', apellidoPaterno: '', apellidoMaterno: '',
  sexo: '', fotoUrl: '',
  email: '', password: '',
  rolId: ROL.RECEPCIONISTA as string,
  telefono: '',
  clinicaId: '', consultorioId: '',
  especialidadNombre: '',
  cedulaProfesional: '',
  institucionFormadora: '',
  cedulaEspecialidad: '',
  activo: true,
};

const inputCls  = 'w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300';
const selectCls = 'px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 bg-white';
const COL       = 'grid grid-cols-[1fr_140px_164px_164px_120px] gap-4 items-center';

const TAB_CLS = [
  'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap',
  'text-text-secondary border-transparent hover:text-text-primary',
  'data-[state=active]:text-primary-600 data-[state=active]:border-primary-500',
].join(' ');


function getNombreCompleto(p: any) {
  return [p.nombre, p.apellidoPaterno, p.apellidoMaterno].filter(Boolean).join(' ');
}

/* ═══════════════════════════════════════════════════════════════════════════ */
export function PersonalPage() {
  const qc = useQueryClient();
  const { usuario } = useAuthStore();

  /* Diálogo */
  const [open, setOpen]           = useState(false);
  const [editing, setEditing]     = useState<any>(null);
  const [verTodasTareas, setVerTodasTareas] = useState(false);
  const [form, setForm]           = useState(emptyForm);
  const [activeTab, setActiveTab] = useState('info');

  /* Crop */
  const fileInputRef                        = useRef<HTMLInputElement>(null);
  const cropperRef                          = useRef<CropperRef>(null);
  const [cropSrc, setCropSrc]               = useState<string | null>(null);
  const [uploading, setUploading]           = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);

  /* Filtros */
  const [search, setSearch]         = useState('');
  const [rolFiltro, setRolFiltro]   = useState('');

  /* Confirmación eliminar */
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const isSuperAdmin = usuario?.rolId === ROL.SUPER_ADMIN;
  const myClinicaId  = usuario?.clinicaId || '';

  /* ─ Queries ─ */
  const { data: personal = [], isLoading } = useQuery({
    queryKey: ['usuarios'],
    queryFn: () => api.get('/api/usuarios').then(r => r.data),
  });

  const { data: consultorios = [] } = useQuery({
    queryKey: ['consultorios-clinica'],
    queryFn: () => api.get('/api/clinica/consultorios').then(r => r.data),
  });

  /* ─ Filtros aplicados ─ */
  const filtered = useMemo(() => (personal as any[]).filter(p => {
    if (search && !getNombreCompleto(p).toLowerCase().includes(search.toLowerCase())) return false;
    if (!isSuperAdmin && p.clinicaId !== myClinicaId) return false;
    if (rolFiltro && p.rolId !== rolFiltro) return false;
    return true;
  }), [personal, search, rolFiltro, isSuperAdmin, myClinicaId]);

  /* Consultorios para el form — el endpoint ya los filtra por clínica de sesión */
  const consultoriosForm = consultorios as any[];

  /* ─ Mutaciones ─ */
  const createMut = useMutation({
    mutationFn: (data: any) => api.post('/api/usuarios', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      qc.invalidateQueries({ queryKey: ['medicos'] });
      toast.success('Personal creado');
      closeDialog();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al crear el personal')),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      api.patch(`/api/usuarios/${id}`, data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      qc.invalidateQueries({ queryKey: ['medicos'] });
      qc.invalidateQueries({ queryKey: ['medicos-select'] });
      qc.invalidateQueries({ queryKey: ['citas-agenda'] });
      toast.success('Personal actualizado');
      closeDialog();
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al actualizar el personal')),
  });

  const verTodasTareasMut = useMutation({
    mutationFn: ({ id, puedeVerTodas }: { id: string; puedeVerTodas: boolean }) =>
      api.patch(`/api/tareas/usuarios/${id}/ver-todas`, { puedeVerTodas }).then(r => r.data),
    onError: (err: any) => toast.error(getErrorMessage(err, 'No se pudo actualizar el acceso a tareas')),
  });

  const toggleActivoMut = useMutation({
    mutationFn: ({ id, activo }: { id: string; activo: boolean }) =>
      api.patch(`/api/usuarios/${id}`, { activo }).then(r => r.data),
    onSuccess: (_, { activo }) => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success(activo ? 'Personal reactivado' : 'Personal suspendido');
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al cambiar estado')),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/api/usuarios/${id}`).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['usuarios'] });
      toast.success('Personal eliminado');
      setDeleteTarget(null);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al eliminar')),
  });

  /* ─ Handlers dialog ─ */
  function closeDialog() {
    setOpen(false);
    setEditing(null);
    if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); }
    setCropSrc(null);
  }

  function openCreate() {
    setEditing(null);
    if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); }
    setForm({ ...emptyForm, clinicaId: isSuperAdmin ? '' : myClinicaId });
    setActiveTab('info');
    setOpen(true);
  }

  function openEdit(u: any) {
    setEditing(u);
    setVerTodasTareas(!!u.puedeVerTodasTareas);
    if (previewBlobUrl) { URL.revokeObjectURL(previewBlobUrl); setPreviewBlobUrl(null); }
    setForm({
      nombre:          u.nombre          || '',
      apellidoPaterno: u.apellidoPaterno || '',
      apellidoMaterno: u.apellidoMaterno || '',
      sexo:            u.sexo            || '',
      fotoUrl:         u.fotoUrl         || '',
      email:           u.email           || '',
      password:        '',
      rolId:           u.rolId,
      telefono:        u.telefono        || '',
      clinicaId:       u.clinicaId       || '',
      consultorioId:   u.consultorioId   || '',
      especialidadNombre: u.especialidad?.nombre || '',
      cedulaProfesional: u.cedulaProfesional || '',
      institucionFormadora: u.institucionFormadora || '',
      cedulaEspecialidad: '',
      activo:          u.activo,
    });
    setActiveTab('info');
    setOpen(true);
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const isMedico = form.rolId === ROL.MEDICO || form.rolId === ROL.MEDICO_ADMIN;
    const payload: any = {
      nombre:          form.nombre,
      apellidoPaterno: form.apellidoPaterno || null,
      apellidoMaterno: form.apellidoMaterno || null,
      sexo:            form.sexo            || null,
      fotoUrl:         form.fotoUrl         || null,
      rolId:           form.rolId,
      telefono:        form.telefono        || null,
      consultorioId:   form.consultorioId   || null,
    };
    if (isMedico) {
      // COFEPRIS / LGS: datos profesionales base (título profesional) — independientes
      // de la especialidad, que se gestiona aparte (EspecialidadesManager / especialidad
      // principal al crear).
      payload.cedulaProfesional    = form.cedulaProfesional    || null;
      payload.institucionFormadora = form.institucionFormadora || null;
    }
    if (editing) {
      payload.activo = form.activo;
      if (form.password) payload.password = form.password;
      const usaTareas = form.rolId === ROL.MEDICO || form.rolId === ROL.ASISTENTE;
      if (usaTareas && verTodasTareas !== !!editing.puedeVerTodasTareas) {
        verTodasTareasMut.mutate({ id: editing.id, puedeVerTodas: verTodasTareas });
      }
      updateMut.mutate({ id: editing.id, data: payload });
    } else {
      payload.email     = form.email;
      payload.password  = form.password || undefined;
      payload.clinicaId = myClinicaId;
      if (isMedico && form.especialidadNombre) {
        payload.especialidadNombre = form.especialidadNombre;
        payload.cedulaEspecialidad = form.cedulaEspecialidad || null;
      }
      createMut.mutate(payload);
    }
  }

  const f = (field: keyof typeof emptyForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const val = field === 'activo' ? (e.target as HTMLInputElement).checked : e.target.value;
      setForm(prev => ({ ...prev, [field]: val }));
    };

  /* ─ Handlers crop ─ */
  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { setCropSrc(reader.result as string); };
    reader.readAsDataURL(file);
    e.target.value = '';
  }

  async function applyCrop() {
    if (!cropSrc || !cropperRef.current) return;
    const canvas = cropperRef.current.getCanvas({ width: 256, height: 256 });
    if (!canvas) return;
    setUploading(true);
    try {
      const blob = await new Promise<Blob>((res, rej) =>
        canvas.toBlob(b => b ? res(b) : rej(new Error('toBlob')), 'image/jpeg', 0.85)
      );

      if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(URL.createObjectURL(blob));

      const fd = new FormData();
      fd.append('file', blob, 'foto-perfil.jpg');

      const clinicaId = isSuperAdmin ? (form.clinicaId || myClinicaId) : myClinicaId;
      const entityId  = editing?.id ?? crypto.randomUUID();
      const paramObj: Record<string, string> = { clinicaId, entityId };
      const rawFotoUrl = form.fotoUrl ? form.fotoUrl.split('?')[0] : null;
      if (rawFotoUrl) paramObj.existingUrl = rawFotoUrl;

      const { data } = await api.post(
        `/api/storage/upload?${new URLSearchParams(paramObj)}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setForm(p => ({ ...p, fotoUrl: data.url }));
      setCropSrc(null);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Error al subir la imagen'));
    } finally {
      setUploading(false);
    }
  }

  const isPending        = createMut.isPending || updateMut.isPending || uploading;
  const hasActiveFilters = search || rolFiltro;

  /* ─────────────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl md:text-2xl font-bold text-text-primary">Personal y Accesos</h2>
          <p className="text-sm text-text-secondary mt-0.5">
            {isLoading ? 'Cargando...' : `${filtered.length} de ${(personal as any[]).length} miembro${(personal as any[]).length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <Plus size={16} /> <span className="hidden sm:inline">Nuevo personal</span><span className="sm:hidden">Nuevo</span>
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre..."
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
          />
        </div>

        <select value={rolFiltro} onChange={e => setRolFiltro(e.target.value)} className={selectCls}>
          <option value="">Todos los roles</option>
          {ROLES_DISPONIBLES.map(r => (
            <option key={r.value} value={r.value}>{r.label}</option>
          ))}
        </select>

        {hasActiveFilters && (
          <button
            onClick={() => { setSearch(''); setRolFiltro(''); }}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={14} /> Limpiar
          </button>
        )}
      </div>

      {/* Lista */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-100 rounded-xl animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
          <Users size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="font-medium text-text-primary">
            {hasActiveFilters ? 'Sin resultados para estos filtros' : 'Sin personal registrado'}
          </p>
          {!hasActiveFilters && (
            <button
              onClick={openCreate}
              className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors"
            >
              Nuevo personal
            </button>
          )}
        </div>
      ) : (
        <>
          {/* ── Vista tabla (desktop ≥768px) ── */}
          <div className="hidden md:block bg-white rounded-xl border border-gray-100 overflow-hidden">
            <div className={`${COL} px-5 py-3 bg-gray-50/80 border-b border-gray-100`}>
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Personal</span>
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Teléfono</span>
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Especialidad</span>
              <span className="text-xs font-semibold text-text-secondary uppercase tracking-wide">Consultorio</span>
              <span />
            </div>
            <div className="divide-y divide-gray-50">
              {filtered.map((p: any) => {
                const nombreCompleto = getNombreCompleto(p);
                const avatarSrc = p.fotoUrl
                  ? `${p.fotoUrl}?v=${p.updatedAt ? new Date(p.updatedAt).getTime() : 0}`
                  : null;
                const consultorioData = p.consultorioId
                  ? (consultorios as any[]).find(c => c.id === p.consultorioId)
                  : null;
                return (
                  <div key={p.id} className={`${COL} px-5 py-4 hover:bg-gray-50/50 transition-colors`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="relative w-10 h-10 flex-shrink-0">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${p.activo === false ? 'bg-gray-100 text-gray-400' : 'bg-primary-100 text-primary-600'}`}>
                          {getInitials(nombreCompleto)}
                        </div>
                        {avatarSrc && (
                          <img src={avatarSrc} alt={nombreCompleto}
                            className="absolute inset-0 w-10 h-10 rounded-full object-cover"
                            onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                        )}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`font-semibold leading-tight truncate ${p.activo === false ? 'text-gray-400' : 'text-text-primary'}`}>{nombreCompleto}</p>
                          {p.activo === false && <span className="text-xs text-gray-400 flex-shrink-0">· Inactivo</span>}
                        </div>
                        <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium border ${ROL_COLOR[p.rolId] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                          {ROL_LABEL[p.rolId] || p.rolId}
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0">
                      {p.telefono ? <span className="text-sm text-text-primary">{p.telefono}</span> : <span className="text-sm text-gray-300">—</span>}
                    </div>
                    <div className="min-w-0">
                      {p.especialidad?.nombre ? (
                        <div className="flex items-center gap-1.5">
                          <Stethoscope size={13} className="text-blue-400 flex-shrink-0" />
                          <span className="text-sm text-text-primary truncate">{p.especialidad.nombre}</span>
                        </div>
                      ) : <span className="text-sm text-gray-300">—</span>}
                    </div>
                    <div className="min-w-0">
                      {consultorioData ? (
                        <div className="flex items-center gap-1.5">
                          <Building2 size={13} className="text-gray-400 flex-shrink-0" />
                          <span className="text-sm text-text-primary truncate">{consultorioData.nombre}</span>
                        </div>
                      ) : <span className="text-sm text-gray-300">—</span>}
                    </div>
                    <div className="flex items-center justify-end gap-1">
                      {p.telefono && (
                        <a href={`tel:${p.telefono}`} title="Llamar"
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                          <Phone size={14} />
                        </a>
                      )}
                      <button onClick={() => openEdit(p)} title="Editar"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => toggleActivoMut.mutate({ id: p.id, activo: !p.activo })}
                        title={p.activo ? 'Suspender' : 'Reactivar'}
                        className={`p-1.5 rounded-lg transition-colors ${p.activo ? 'text-gray-400 hover:text-amber-600 hover:bg-amber-50' : 'text-amber-500 hover:text-green-600 hover:bg-green-50'}`}>
                        {p.activo ? <PauseCircle size={14} /> : <PlayCircle size={14} />}
                      </button>
                      <button onClick={() => setDeleteTarget(p)} title="Eliminar"
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Vista cards (mobile <768px) ── */}
          <div className="md:hidden space-y-2.5">
            {filtered.map((p: any) => {
              const nombreCompleto = getNombreCompleto(p);
              const avatarSrc = p.fotoUrl
                ? `${p.fotoUrl}?v=${p.updatedAt ? new Date(p.updatedAt).getTime() : 0}`
                : null;
              const consultorioData = p.consultorioId
                ? (consultorios as any[]).find(c => c.id === p.consultorioId)
                : null;
              return (
                <div key={p.id} className="bg-white rounded-xl border border-gray-100 p-4">
                  {/* Row superior: avatar + nombre + acciones */}
                  <div className="flex items-start gap-3">
                    <div className="relative w-11 h-11 flex-shrink-0">
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-bold ${p.activo === false ? 'bg-gray-100 text-gray-400' : 'bg-primary-100 text-primary-600'}`}>
                        {getInitials(nombreCompleto)}
                      </div>
                      {avatarSrc && (
                        <img src={avatarSrc} alt={nombreCompleto}
                          className="absolute inset-0 w-11 h-11 rounded-full object-cover"
                          onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <p className={`font-semibold leading-tight ${p.activo === false ? 'text-gray-400' : 'text-text-primary'}`}>
                          {nombreCompleto}
                        </p>
                        {p.activo === false && <span className="text-xs text-gray-400">· Inactivo</span>}
                      </div>
                      <span className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-xs font-medium border ${ROL_COLOR[p.rolId] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                        {ROL_LABEL[p.rolId] || p.rolId}
                      </span>
                    </div>
                    {/* Acciones compactas */}
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <button onClick={() => openEdit(p)} className="p-2 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50 transition-colors">
                        <Pencil size={15} />
                      </button>
                      <button onClick={() => setDeleteTarget(p)} className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors">
                        <Trash2 size={15} />
                      </button>
                    </div>
                  </div>

                  {/* Detalles */}
                  {(p.telefono || p.especialidad?.nombre || consultorioData) && (
                    <div className="mt-3 pt-3 border-t border-gray-50 space-y-1.5">
                      {p.telefono && (
                        <a href={`tel:${p.telefono}`} className="flex items-center gap-2 text-sm text-text-secondary">
                          <Phone size={13} className="text-gray-400 flex-shrink-0" />
                          {p.telefono}
                        </a>
                      )}
                      {p.especialidad?.nombre && (
                        <div className="flex items-center gap-2 text-sm text-text-secondary">
                          <Stethoscope size={13} className="text-blue-400 flex-shrink-0" />
                          {p.especialidad.nombre}
                        </div>
                      )}
                      {consultorioData && (
                        <div className="flex items-center gap-2 text-sm text-text-secondary">
                          <Building2 size={13} className="text-gray-400 flex-shrink-0" />
                          {consultorioData.nombre}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Toggle activo */}
                  <button
                    onClick={() => toggleActivoMut.mutate({ id: p.id, activo: !p.activo })}
                    className={`mt-3 w-full py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                      p.activo
                        ? 'border-amber-200 text-amber-700 hover:bg-amber-50'
                        : 'border-green-200 text-green-700 hover:bg-green-50'
                    }`}
                  >
                    {p.activo ? 'Suspender acceso' : 'Reactivar acceso'}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* ── Dialog confirmar eliminación ─────────────────────────────────── */}
      <Dialog.Root open={!!deleteTarget} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 bg-white rounded-2xl shadow-xl w-full max-w-sm p-6 focus:outline-none">
            <div className="flex flex-col items-center text-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                <AlertTriangle size={22} className="text-red-500" />
              </div>
              <div>
                <Dialog.Title className="text-base font-semibold text-text-primary">
                  ¿Eliminar a {deleteTarget ? getNombreCompleto(deleteTarget) : ''}?
                </Dialog.Title>
                <p className="text-sm text-text-secondary mt-1">
                  Esta persona dejará de aparecer en la lista. Sus datos históricos se conservan.
                </p>
              </div>
              <div className="flex gap-3 w-full">
                <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                  Cancelar
                </Dialog.Close>
                <button
                  onClick={() => deleteTarget && deleteMut.mutate(deleteTarget.id)}
                  disabled={deleteMut.isPending}
                  className="flex-1 py-2.5 rounded-lg bg-red-500 text-white text-sm font-medium hover:bg-red-600 transition-colors disabled:opacity-60"
                >
                  {deleteMut.isPending ? 'Eliminando...' : 'Sí, eliminar'}
                </button>
              </div>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      {/* ── Dialog crear / editar ────────────────────────────────────────── */}
      <Dialog.Root open={open} onOpenChange={v => { if (!v) closeDialog(); }}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" />
          <Dialog.Content className="fixed inset-x-0 bottom-0 md:inset-x-auto md:left-1/2 md:bottom-auto md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 z-50 bg-white rounded-t-3xl md:rounded-2xl shadow-xl w-full md:max-w-lg focus:outline-none flex flex-col max-h-[92vh] md:max-h-[90vh]">

            <form onSubmit={handleSubmit} className="flex flex-col min-h-0">

              {cropSrc ? (
                /* ── Vista de recorte ────────────────────────────────────── */
                <>
                  <div className="flex items-center justify-between px-6 pt-5 pb-4 flex-shrink-0">
                    <span className="text-base font-semibold text-text-primary">Recortar foto de perfil</span>
                    <button type="button" onClick={() => setCropSrc(null)} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                      <X size={18} />
                    </button>
                  </div>

                  <div className="mx-6 rounded-xl overflow-hidden" style={{ height: 320 }}>
                    <Cropper
                      ref={cropperRef}
                      src={cropSrc!}
                      stencilComponent={CircleStencil}
                      style={{ height: 320 }}
                    />
                  </div>

                  <div className="flex items-center justify-end gap-4 px-6 py-4 flex-shrink-0">
                    <div className="flex gap-2 flex-shrink-0">
                      <button type="button" onClick={() => setCropSrc(null)} disabled={uploading}
                        className="px-4 py-2 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors disabled:opacity-60">
                        Cancelar
                      </button>
                      <button type="button" onClick={applyCrop} disabled={uploading}
                        className="px-4 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium transition-colors disabled:opacity-60">
                        {uploading ? 'Subiendo...' : 'Aplicar'}
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                /* ── Vista normal ────────────────────────────────────────── */
                <>
                  <div className="flex items-center justify-between px-6 pt-6 pb-0 flex-shrink-0">
                    <Dialog.Title className="text-lg font-semibold text-text-primary">
                      {editing ? 'Editar personal' : 'Nuevo personal'}
                    </Dialog.Title>
                    <Dialog.Close className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors">
                      <X size={18} />
                    </Dialog.Close>
                  </div>

                  <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="flex flex-col min-h-0 mt-4">
                    <Tabs.List className="flex border-b border-gray-100 px-6 flex-shrink-0 overflow-x-auto">
                      <Tabs.Trigger value="info"         className={TAB_CLS}>Información</Tabs.Trigger>
                      <Tabs.Trigger value="acceso"       className={TAB_CLS}>Acceso</Tabs.Trigger>
                      <Tabs.Trigger value="asignacion"   className={TAB_CLS}>Asignación</Tabs.Trigger>
                      {(form.rolId === ROL.MEDICO || form.rolId === ROL.MEDICO_ADMIN) && (
                        <Tabs.Trigger value="profesional" className={TAB_CLS}>Profesional</Tabs.Trigger>
                      )}
                    </Tabs.List>

                    <div className="overflow-y-auto">

                      {/* ── Tab: Información ─────────────────────────── */}
                      <Tabs.Content value="info" className="p-6 space-y-4 outline-none">

                        {/* Foto */}
                        <div className="flex flex-col items-center gap-3">
                          <input
                            ref={fileInputRef} type="file" accept="image/*"
                            className="hidden" onChange={handleFileSelect}
                          />
                          <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            className="relative w-24 h-24 rounded-full group focus:outline-none"
                            title="Cambiar foto"
                          >
                            <div className="w-24 h-24 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-2xl font-bold">
                              {getInitials(getNombreCompleto(form)) || <Camera size={28} className="text-primary-400" />}
                            </div>
                            {(previewBlobUrl || form.fotoUrl) && (
                              <img
                                src={previewBlobUrl || form.fotoUrl}
                                alt="Foto"
                                className="absolute inset-0 w-24 h-24 rounded-full object-cover"
                                onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            )}
                            <div className="absolute inset-0 rounded-full bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                              <Camera size={22} className="text-white" />
                            </div>
                          </button>
                          <span className="text-xs text-text-secondary">Haz clic para subir foto · Proporción 1:1</span>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-1">
                            Nombre(s) <span className="text-red-500">*</span>
                          </label>
                          <input required value={form.nombre} onChange={f('nombre')} placeholder="Ana Karen" className={inputCls} />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium text-text-primary mb-1">Apellido paterno</label>
                            <input value={form.apellidoPaterno} onChange={f('apellidoPaterno')} placeholder="Martínez" className={inputCls} />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-text-primary mb-1">Apellido materno</label>
                            <input value={form.apellidoMaterno} onChange={f('apellidoMaterno')} placeholder="López" className={inputCls} />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-1">Sexo</label>
                          <select value={form.sexo} onChange={f('sexo')} className={`${inputCls} bg-white`}>
                            <option value="">No especificado</option>
                            {SEXO_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-1">Teléfono</label>
                          <input type="tel" value={form.telefono} onChange={f('telefono')} placeholder="55 1234 5678" className={inputCls} />
                        </div>

                      </Tabs.Content>

                      {/* ── Tab: Acceso ──────────────────────────────── */}
                      <Tabs.Content value="acceso" className="p-6 space-y-4 outline-none">

                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-1">
                            Correo <span className="text-red-500">*</span>
                          </label>
                          <input
                            required type="email" value={form.email} onChange={f('email')}
                            placeholder="usuario@clinica.com" disabled={!!editing}
                            className={`${inputCls} disabled:bg-gray-50 disabled:text-gray-500`}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-1">
                            {editing ? 'Nueva contraseña' : 'Contraseña inicial'} <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="password" value={form.password} onChange={f('password')}
                            placeholder={editing ? 'Dejar vacío para no cambiar' : 'Ingresa una contraseña'}
                            className={inputCls}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-1">
                            Rol <span className="text-red-500">*</span>
                          </label>
                          {editing?.rolId === ROL.SUPER_ADMIN ? (
                            <div className={`${inputCls} bg-gray-50 text-gray-500 cursor-not-allowed`}>
                              Super Admin <span className="text-xs ml-1">(rol del sistema)</span>
                            </div>
                          ) : (
                            <select required value={form.rolId} onChange={f('rolId')} className={`${inputCls} bg-white`}>
                              {ROLES_DISPONIBLES.map(r => (
                                <option key={r.value} value={r.value}>{r.label}</option>
                              ))}
                            </select>
                          )}
                        </div>

                        {editing && (
                          <label className="flex items-center gap-3 cursor-pointer pt-1">
                            <input type="checkbox" checked={form.activo} onChange={f('activo')} className="w-4 h-4 rounded accent-primary-500" />
                            <span className="flex items-center gap-1.5 text-sm font-medium text-text-primary">
                              {form.activo
                                ? <UserCheck size={16} className="text-green-500" />
                                : <UserX size={16} className="text-gray-400" />}
                              {form.activo ? 'Personal activo' : 'Personal inactivo (suspendido)'}
                            </span>
                          </label>
                        )}

                        {editing && (form.rolId === ROL.MEDICO || form.rolId === ROL.ASISTENTE) && (
                          <label className="flex items-start gap-3 cursor-pointer pt-1">
                            <input type="checkbox" checked={verTodasTareas} onChange={e => setVerTodasTareas(e.target.checked)} className="w-4 h-4 mt-0.5 rounded accent-primary-500" />
                            <span className="text-sm text-text-primary">
                              <span className="font-medium">Puede ver todas las tareas de la clínica</span>
                              <span className="block text-xs text-text-secondary">Si no, solo ve las tareas que creó o que le asignaron.</span>
                            </span>
                          </label>
                        )}

                      </Tabs.Content>

                      {/* ── Tab: Asignación ──────────────────────────── */}
                      <Tabs.Content value="asignacion" className="p-6 space-y-4 outline-none">
                        <div>
                          <label className="block text-sm font-medium text-text-primary mb-1">
                            Consultorio <span className="text-text-secondary font-normal">(opcional)</span>
                          </label>
                          <select
                            value={form.consultorioId}
                            onChange={f('consultorioId')}
                            className={`${inputCls} bg-white`}
                          >
                            <option value="">Sin asignar</option>
                            {consultoriosForm.map((c: any) => (
                              <option key={c.id} value={c.id}>{c.nombre}</option>
                            ))}
                          </select>
                        </div>
                      </Tabs.Content>

                      {/* ── Tab: Datos profesionales (solo médicos) ──── */}
                      {(form.rolId === ROL.MEDICO || form.rolId === ROL.MEDICO_ADMIN) && (
                        <Tabs.Content value="profesional" className="p-6 space-y-4 outline-none">
                          {/* Datos base del título profesional — no dependen de la especialidad */}
                          <div className="space-y-3 pb-4 border-b border-gray-100">
                            <div>
                              <label className="block text-sm font-medium text-text-primary mb-1">
                                Cédula profesional <span className="text-text-secondary font-normal">(opcional)</span>
                              </label>
                              <input
                                value={form.cedulaProfesional}
                                onChange={f('cedulaProfesional')}
                                placeholder="Número de cédula"
                                className={inputCls}
                                maxLength={100}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-text-primary mb-1">
                                Institución formadora <span className="text-text-secondary font-normal">(opcional)</span>
                              </label>
                              <input
                                value={form.institucionFormadora}
                                onChange={f('institucionFormadora')}
                                placeholder="ej. UNAM, BUAP..."
                                className={inputCls}
                                maxLength={255}
                              />
                            </div>
                            <p className="text-xs text-text-secondary">
                              Datos obligatorios ante COFEPRIS para la emisión de recetas digitales.
                            </p>
                          </div>

                          {editing ? (
                            /* Editando: CRUD completo de especialidades. Cada especialidad
                               tiene su propia cédula e institución formadora (COFEPRIS / LGS). */
                            <EspecialidadesManager usuarioId={editing.id} />
                          ) : (
                            /* Creando: primera especialidad (opcional) */
                            <div className="space-y-3">
                              <p className="text-xs text-text-secondary">
                                Puedes agregar la especialidad principal ahora o hacerlo después desde este mismo panel.
                              </p>
                              <div>
                                <label className="block text-sm font-medium text-text-primary mb-1">
                                  Especialidad principal <span className="text-text-secondary font-normal">(opcional)</span>
                                </label>
                                <ComboboxInput
                                  options={ESPECIALIDADES}
                                  value={form.especialidadNombre}
                                  onChange={v => setForm(p => ({ ...p, especialidadNombre: v }))}
                                  placeholder="Buscar especialidad..."
                                  className={`${inputCls} bg-white`}
                                  freeText={false}
                                />
                              </div>
                              {form.especialidadNombre && (
                                <div>
                                  <label className="block text-sm font-medium text-text-primary mb-1">
                                    Cédula de especialidad <span className="text-text-secondary font-normal">(opcional)</span>
                                  </label>
                                  <input
                                    value={form.cedulaEspecialidad}
                                    onChange={f('cedulaEspecialidad')}
                                    placeholder="Número de cédula de especialidad"
                                    className={inputCls}
                                    maxLength={100}
                                  />
                                  <p className="text-xs text-text-secondary mt-1">
                                    La institución formadora de esta especialidad se agrega después desde este mismo panel.
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </Tabs.Content>
                      )}

                    </div>
                  </Tabs.Root>

                  <div className="flex gap-3 px-6 py-4 border-t border-gray-100 flex-shrink-0">
                    <Dialog.Close className="flex-1 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-text-primary hover:bg-gray-50 transition-colors">
                      Cancelar
                    </Dialog.Close>
                    <button type="submit" disabled={isPending} className="flex-1 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors disabled:opacity-60">
                      {isPending ? 'Guardando...' : editing ? 'Guardar cambios' : 'Crear personal'}
                    </button>
                  </div>
                </>
              )}

            </form>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

    </div>
  );
}
